import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Filter, X, ChevronDown, ChevronUp,
  ShieldAlert, ShieldCheck, ShieldX, AlertTriangle,
  Loader2, RefreshCw, Package, Download, Copy,
  Eye, EyeOff, Zap, ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';
import { useSeller } from '../../context/SellerContext';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';

// ─── Status Configuration ────────────────────────────────────────────────────

const HIJACK_STATUS = {
  SAFE: { label: 'Safe', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  HIJACKED_BUYBOX_RETAINED: { label: 'BB Won', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  HIJACKED_BUYBOX_LOST: { label: 'BB Lost', color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
  HIJACKED_BUYBOX_UNKNOWN: { label: 'BB Unknown', color: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200' },
  BUYBOX_SUPPRESSED: { label: 'Suppressed', color: 'text-violet-700', bg: 'bg-violet-50', border: 'border-violet-200' },
};

const LISTING_STATUSES = ['ACTIVE', 'INACTIVE', 'INCOMPLETE', 'CLOSED', 'DELETED'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(val) {
  if (val == null) return '—';
  return `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function parseCompetingSellers(raw) {
  if (!raw) return [];
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch { return []; }
}

// ─── Status Badge ────────────────────────────────────────────────────────────

function StatusBadge({ status, suppressedReason }) {
  const hs = HIJACK_STATUS[status] || HIJACK_STATUS.SAFE;
  return (
    <div>
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${hs.bg} ${hs.color} ${hs.border} border`}>
        {hs.label}
      </span>
      {suppressedReason && (
        <p className="text-[9px] text-violet-500 mt-0.5 truncate max-w-[120px]" title={suppressedReason}>
          {suppressedReason}
        </p>
      )}
    </div>
  );
}

// ─── Detail Panel ────────────────────────────────────────────────────────────

function DetailPanel({ alert, onClose }) {
  if (!alert) return null;
  const hs = HIJACK_STATUS[alert.status] || HIJACK_STATUS.SAFE;
  const sellers = parseCompetingSellers(alert.competingSellers);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/20" />
      <div
        className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between z-10">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-slate-900 truncate">{alert.title || 'Untitled Product'}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm font-bold text-slate-700 font-mono">{alert.asin}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(alert.asin); toast.success('ASIN copied'); }}
                className="text-slate-300 hover:text-slate-600 transition-colors"
                title="Copy ASIN"
              >
                <Copy size={12} />
              </button>
              <a
                href={`https://www.amazon.in/dp/${alert.asin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-[10px] text-[#f08804] hover:text-[#C45500] font-medium"
              >
                <ExternalLink size={10} /> Amazon
              </a>
            </div>
          </div>
          <button onClick={onClose} className="ml-4 text-slate-400 hover:text-slate-600 p-1">
            <X size={18} />
          </button>
        </div>

        <div className="p-3 sm:p-5 space-y-4">
          {/* Status */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Status</p>
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border ${hs.bg} ${hs.color} ${hs.border}`}>
              {hs.label}
            </span>
            {alert.suppressedReason && (
              <div className="mt-2 bg-violet-50 border border-violet-200 rounded-lg p-3">
                <p className="text-[10px] font-bold text-violet-600 uppercase mb-0.5">Reason</p>
                <p className="text-xs text-violet-700">{alert.suppressedReason}</p>
              </div>
            )}
          </div>

          {/* Pricing Grid */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Pricing</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Your Price" value={formatPrice(alert.yourPrice)} />
              <Field label="Buy Box Price" value={formatPrice(alert.buyBoxPrice)} />
              <Field label="Price Diff" value={alert.priceDiff != null ? formatPrice(alert.priceDiff) : '—'} />
              <Field label="Diff %" value={alert.priceDiffPercent != null ? `${Number(alert.priceDiffPercent).toFixed(1)}%` : '—'} />
              <Field label="Lowest FBA" value={formatPrice(alert.lowestFbaPrice)} />
              <Field label="Lowest FBM" value={formatPrice(alert.lowestFbmPrice)} />
            </div>
          </div>

          {/* Product Info */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Product Info</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="ASIN" value={alert.asin} mono />
              <Field label="SKU" value={alert.sku} mono />
              <Field label="Listing Status" value={alert.listingStatus || '—'} />
              <Field label="Total Sellers" value={alert.totalSellers} />
              <Field label="Buy Box" value={alert.buyBoxIsYours ? 'You have it' : 'Someone else'} />
              <Field label="Fulfillment" value={alert.buyBoxFulfillment || '—'} />
            </div>
          </div>

          {/* Timestamps */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Timeline</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Last Checked" value={formatDateTime(alert.lastCheckedAt)} />
              <Field label="First Detected" value={alert.firstDetectedAt ? formatDateTime(alert.firstDetectedAt) : '—'} />
              {alert.resolvedAt && <Field label="Resolved" value={formatDateTime(alert.resolvedAt)} />}
            </div>
          </div>

          {/* Competing Sellers */}
          {sellers.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                Competing Sellers ({sellers.length})
              </p>
              <div className="space-y-2">
                {sellers.map((s, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <span className="text-[11px] font-mono text-slate-600 truncate flex-1" title={s.sellerId}>
                      {s.sellerId}
                    </span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-[11px] font-semibold text-slate-700">
                        {s.price != null ? formatPrice(s.price) : '—'}
                      </span>
                      <span className="text-[9px] text-slate-400 uppercase w-8">
                        {s.fulfillmentType === 'AFN' ? 'FBA' : s.fulfillmentType === 'MFN' ? 'FBM' : s.fulfillmentType || '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-[12px] font-medium text-slate-700 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
    </div>
  );
}

// ─── Mini Stat ───────────────────────────────────────────────────────────────

function MiniStat({ label, value, color, bg, border, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border transition-all flex-shrink-0 ${
        active
          ? `ring-2 ring-[#f08804] border-[#FFD89E] shadow-sm ${bg}`
          : `${border} ${bg} hover:shadow-sm`
      }`}
    >
      <span className="text-base font-bold text-slate-800 leading-none">{value}</span>
      <span className={`text-[9px] font-semibold uppercase tracking-wider leading-none ${color}`}>{label}</span>
    </button>
  );
}

// ─── Export CSV ───────────────────────────────────────────────────────────────

function exportToCsv(data) {
  const headers = ['ASIN', 'Title', 'SKU', 'Status', 'Suppressed Reason', 'Listing Status', 'Your Price', 'Buy Box Price', 'Price Diff', 'Diff %', 'Total Sellers', 'Buy Box Yours', 'Lowest FBA', 'Lowest FBM', 'Last Checked', 'Seller'];
  const rows = data.map(a => [
    a.asin, `"${(a.title || '').replace(/"/g, '""')}"`, a.sku || '',
    a.status, a.suppressedReason || '', a.listingStatus || '',
    a.yourPrice ?? '', a.buyBoxPrice ?? '', a.priceDiff ?? '', a.priceDiffPercent ?? '',
    a.totalSellers ?? '', a.buyBoxIsYours ? 'Yes' : 'No',
    a.lowestFbaPrice ?? '', a.lowestFbmPrice ?? '',
    a.lastCheckedAt || '', a.sellerName || '',
  ]);
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `hijack-alerts-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Sort Icon ───────────────────────────────────────────────────────────────

function SortIcon({ field, sortBy, sortDir }) {
  if (sortBy !== field) return <ChevronDown size={14} className="text-gray-300" />;
  return sortDir === 'asc'
    ? <ChevronUp size={14} className="text-[#C45500]" />
    : <ChevronDown size={14} className="text-[#C45500]" />;
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

export default function HijackAlertsPage() {
  const { selectedSeller: sellerCtx } = useSeller();
  const sellerId = sellerCtx?.id || '';
  const sellerChannels = useMemo(() => (sellerCtx?.channels || []).filter(c => c.accountStatus === 'AUTHORIZED'), [sellerCtx]);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBuyBox, setFilterBuyBox] = useState('');
  const [filterMinSellers, setFilterMinSellers] = useState('');
  const [filterListingStatus, setFilterListingStatus] = useState('');
  const [sortBy, setSortBy] = useState('lastCheckedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  // Derive filtered accounts based on selectedAccountId
  const filteredAccounts = useMemo(() => {
    if (selectedAccountId) return sellerChannels.filter(a => a.id === selectedAccountId);
    return sellerChannels;
  }, [sellerChannels, selectedAccountId]);

  // Build API params — always send sellerId, optionally sellerAccountId
  const apiParams = useMemo(() => {
    const params = new URLSearchParams();
    if (sellerId) params.append('sellerId', sellerId);
    if (selectedAccountId) params.append('sellerAccountId', selectedAccountId);
    return params;
  }, [sellerId, selectedAccountId]);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const queryStr = apiParams.toString();
      const url = `${ENDPOINTS.HIJACK_ALERTS.GET_ALL}${queryStr ? `?${queryStr}` : ''}`;
      const res = await apiClient.get(url);
      const data = res.data || res;
      setAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load hijack alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [apiParams]);

  // Run live hijack check for filtered accounts
  const handleRunCheck = useCallback(async () => {
    if (filteredAccounts.length === 0) {
      toast.error('No authorized accounts found for selection');
      return;
    }
    setChecking(true);
    let totalChecked = 0, totalIssues = 0;
    for (const account of filteredAccounts) {
      try {
        toast(`Checking ${account.sellerName || account.id}...`, { icon: '🔍', duration: 2000 });
        const res = await apiClient.post(`${ENDPOINTS.REPORTS.HIJACK_CHECK}?sellerAccountId=${account.id}`);
        const data = res.data || res;
        totalChecked += data.totalAsinsChecked || 0;
        totalIssues += (data.hijackedBuyBoxLostCount || 0) + (data.hijackedBuyBoxRetainedCount || 0) + (data.buyBoxSuppressedCount || 0);
      } catch (err) {
        toast.error(`Failed: ${account.sellerName || account.id}`);
      }
    }
    if (totalIssues > 0) toast.error(`${totalIssues} issue${totalIssues > 1 ? 's' : ''} found across ${totalChecked} ASINs`);
    else if (totalChecked > 0) toast.success(`All clear! ${totalChecked} ASINs checked`);
    await loadAlerts();
    setChecking(false);
  }, [filteredAccounts, loadAlerts]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // Stats from full (unfiltered) dataset
  const stats = useMemo(() => {
    const total = alerts.length;
    const safe = alerts.filter(a => a.status === 'SAFE').length;
    const bbLost = alerts.filter(a => a.status === 'HIJACKED_BUYBOX_LOST').length;
    const bbRetained = alerts.filter(a => a.status === 'HIJACKED_BUYBOX_RETAINED').length;
    const bbUnknown = alerts.filter(a => a.status === 'HIJACKED_BUYBOX_UNKNOWN').length;
    const suppressed = alerts.filter(a => a.status === 'BUYBOX_SUPPRESSED').length;
    return { total, safe, bbLost, bbRetained, suppressed };
  }, [alerts]);

  // Client-side filters + sort
  const filtered = useMemo(() => {
    let result = alerts;
    if (filterStatus) result = result.filter(a => a.status === filterStatus);
    if (filterBuyBox === 'WON') result = result.filter(a => a.buyBoxIsYours === true);
    else if (filterBuyBox === 'LOST') result = result.filter(a => a.buyBoxIsYours === false);
    if (filterMinSellers) result = result.filter(a => (a.totalSellers || 0) >= parseInt(filterMinSellers));
    if (filterListingStatus) result = result.filter(a => a.listingStatus === filterListingStatus);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.title?.toLowerCase().includes(q) || a.asin?.toLowerCase().includes(q) ||
        a.sku?.toLowerCase().includes(q) || a.sellerName?.toLowerCase().includes(q)
      );
    }
    // Sort
    result = [...result].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (va == null) return 1; if (vb == null) return -1;
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [alerts, filterStatus, filterBuyBox, filterMinSellers, filterListingStatus, search, sortBy, sortDir]);

  const hasActiveFilters = filterStatus || filterBuyBox || filterMinSellers || filterListingStatus || search;
  const clearFilters = () => {
    setFilterStatus(''); setFilterBuyBox(''); setFilterMinSellers(''); setFilterListingStatus(''); setSearch('');
  };

  const handleStatClick = (status) => setFilterStatus(filterStatus === status ? '' : status);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const selectCls = "px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]";

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Hijack Monitor</h1>
            <p className="text-xs text-slate-400 mt-0.5">Buy box hijack detection across your Amazon listings</p>
          </div>
          <div className="flex items-center gap-2">
            <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} />
            <button onClick={loadAlerts} disabled={loading}
              className="p-2.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-50" title="Refresh">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={handleRunCheck}
              disabled={checking || loading || !sellerId}
              title={!sellerId ? 'Select a seller first' : ''}
              className="inline-flex items-center px-4 py-2 bg-[#232F3E] text-white rounded-lg text-sm font-medium hover:bg-[#37475A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checking ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Zap size={16} className="mr-2" />}
              {checking ? 'Checking...' : 'Run Live Check'}
            </button>
            <button
              onClick={() => exportToCsv(filtered)}
              disabled={filtered.length === 0}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Download size={16} className="mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Stat badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <MiniStat label="Total" value={stats.total} color="text-[#C45500]" bg="bg-[#FFF4E6]" border="border-[#FFD89E]" active={filterStatus === ''} onClick={() => handleStatClick('')} />
          <MiniStat label="Safe" value={stats.safe} color="text-emerald-600" bg="bg-emerald-50" border="border-emerald-200" active={filterStatus === 'SAFE'} onClick={() => handleStatClick('SAFE')} />
          <MiniStat label="BB Lost" value={stats.bbLost} color="text-rose-600" bg="bg-rose-50" border="border-rose-200" active={filterStatus === 'HIJACKED_BUYBOX_LOST'} onClick={() => handleStatClick('HIJACKED_BUYBOX_LOST')} />
          <MiniStat label="BB Retained" value={stats.bbRetained} color="text-amber-600" bg="bg-amber-50" border="border-amber-200" active={filterStatus === 'HIJACKED_BUYBOX_RETAINED'} onClick={() => handleStatClick('HIJACKED_BUYBOX_RETAINED')} />
          {stats.bbUnknown > 0 && <MiniStat label="BB Unknown" value={stats.bbUnknown} color="text-slate-600" bg="bg-slate-50" border="border-slate-200" active={filterStatus === 'HIJACKED_BUYBOX_UNKNOWN'} onClick={() => handleStatClick('HIJACKED_BUYBOX_UNKNOWN')} />}
          <MiniStat label="Suppressed" value={stats.suppressed} color="text-violet-600" bg="bg-violet-50" border="border-violet-200" active={filterStatus === 'BUYBOX_SUPPRESSED'} onClick={() => handleStatClick('BUYBOX_SUPPRESSED')} />
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ASIN, product, SKU, seller..."
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] w-[260px]"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                  <X size={14} />
                </button>
              )}
            </div>

            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={selectCls}>
              <option value="">All Statuses</option>
              <option value="SAFE">Safe</option>
              <option value="HIJACKED_BUYBOX_LOST">BB Lost</option>
              <option value="HIJACKED_BUYBOX_UNKNOWN">BB Unknown</option>
              <option value="HIJACKED_BUYBOX_RETAINED">BB Retained</option>
              <option value="BUYBOX_SUPPRESSED">Suppressed</option>
            </select>

            <select value={filterListingStatus} onChange={(e) => setFilterListingStatus(e.target.value)} className={selectCls}>
              <option value="">All Listings</option>
              {LISTING_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <select value={filterBuyBox} onChange={(e) => setFilterBuyBox(e.target.value)} className={selectCls}>
              <option value="">Buy Box: All</option>
              <option value="WON">Buy Box Won</option>
              <option value="LOST">Buy Box Lost</option>
            </select>

            <select value={filterMinSellers} onChange={(e) => setFilterMinSellers(e.target.value)} className={selectCls}>
              <option value="">Sellers: Any</option>
              <option value="2">2+ Sellers</option>
              <option value="3">3+ Sellers</option>
              <option value="5">5+ Sellers</option>
            </select>

            {hasActiveFilters && (
              <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800">
                <X size={14} className="mr-1" /> Clear
              </button>
            )}

            <span className="ml-auto text-sm text-gray-500">
              {filtered.length} of {alerts.length} products
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[50px]"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('asin')}>
                    <div className="flex items-center gap-1">ASIN <SortIcon field="asin" sortBy={sortBy} sortDir={sortDir} /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('title')}>
                    <div className="flex items-center gap-1">Product <SortIcon field="title" sortBy={sortBy} sortDir={sortDir} /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Listing</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('yourPrice')}>
                    <div className="flex items-center justify-end gap-1">Your Price <SortIcon field="yourPrice" sortBy={sortBy} sortDir={sortDir} /></div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('buyBoxPrice')}>
                    <div className="flex items-center justify-end gap-1">Buy Box <SortIcon field="buyBoxPrice" sortBy={sortBy} sortDir={sortDir} /></div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Diff</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('totalSellers')}>
                    <div className="flex items-center justify-center gap-1">Sellers <SortIcon field="totalSellers" sortBy={sortBy} sortDir={sortDir} /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Checked</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 11 }).map((_, j) => (
                        <td key={j} className="px-4 py-4">
                          <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${50 + Math.random() * 50}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-6 py-16 text-center">
                      <ShieldAlert size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 text-lg font-medium">No hijack alerts</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {hasActiveFilters ? 'Try adjusting your filters.' : 'Select a seller account and click "Run Live Check".'}
                      </p>
                      {hasActiveFilters && (
                        <button onClick={clearFilters} className="mt-3 text-sm font-medium text-[#C45500] hover:text-[#C45500]">Clear all filters</button>
                      )}
                    </td>
                  </tr>
                ) : (
                  filtered.map((a) => {
                    const priceDiffNeg = a.priceDiff != null && a.priceDiff < 0;
                    return (
                      <tr key={a.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedAlert(a)}>
                        {/* Image */}
                        <td className="px-4 py-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
                            {a.imageUrl ? (
                              <img src={a.imageUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <Package size={14} className="text-slate-400" />
                            )}
                          </div>
                        </td>
                        {/* ASIN */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-[#C45500] font-mono">{a.asin}</span>
                            <a
                              href={`https://www.amazon.in/dp/${a.asin}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-gray-300 hover:text-[#f08804]"
                              title="View on Amazon"
                            >
                              <ExternalLink size={12} />
                            </a>
                          </div>
                        </td>
                        {/* Title */}
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700 truncate max-w-[200px]" title={a.title}>
                            {a.title || '—'}
                          </p>
                          {a.sellerName && <p className="text-xs text-gray-400">{a.sellerName}</p>}
                        </td>
                        {/* Status */}
                        <td className="px-4 py-3">
                          <StatusBadge status={a.status} suppressedReason={a.suppressedReason} />
                        </td>
                        {/* Listing Status */}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                            a.listingStatus === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' :
                            a.listingStatus === 'INACTIVE' ? 'bg-gray-100 text-gray-600' :
                            a.listingStatus === 'INCOMPLETE' ? 'bg-amber-50 text-amber-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {a.listingStatus || '—'}
                          </span>
                        </td>
                        {/* Your Price */}
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">
                          {formatPrice(a.yourPrice)}
                        </td>
                        {/* Buy Box Price */}
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">
                          {formatPrice(a.buyBoxPrice)}
                        </td>
                        {/* Diff */}
                        <td className="px-4 py-3 text-right">
                          {a.priceDiff != null ? (
                            <div>
                              <span className={`text-sm font-semibold ${priceDiffNeg ? 'text-rose-600' : 'text-emerald-600'}`}>
                                {priceDiffNeg ? '' : '+'}{formatPrice(a.priceDiff)}
                              </span>
                              {a.priceDiffPercent != null && (
                                <p className={`text-[10px] ${priceDiffNeg ? 'text-rose-400' : 'text-emerald-400'}`}>
                                  {priceDiffNeg ? '' : '+'}{Number(a.priceDiffPercent).toFixed(1)}%
                                </p>
                              )}
                            </div>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        {/* Sellers */}
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm font-bold ${(a.totalSellers || 0) > 1 ? 'text-amber-600' : 'text-gray-500'}`}>
                            {a.totalSellers || 0}
                          </span>
                        </td>
                        {/* Checked */}
                        <td className="px-4 py-3">
                          <p className="text-xs text-gray-500">{timeAgo(a.lastCheckedAt)}</p>
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedAlert(a); }}
                            className="text-gray-400 hover:text-[#C45500] p-1"
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedAlert && (
        <DetailPanel alert={selectedAlert} onClose={() => setSelectedAlert(null)} />
      )}
    </AdminLayout>
  );
}
